// Types for the Outreach data

// YouTube sort order options for search results
// Note: viewCount is the only valid API-supported numeric sort beyond relevance and title.
// YouTube API does not support sorting by subscriber count.
export type YouTubeSortOrder = 'relevance' | 'viewCount' | 'title';

// Pagination state interface for managing search pagination
export interface PaginationState {
  currentPage: number;
  totalPages?: number;
  pageTokens: Map<number, string>;
  resultsPerPage: number;
}

// Base type for all outreach items
export interface OutreachItemBase {
  id: string;
  genre: string;
}

/**
 * YouTube channel search result data used across outreach features.
 * 
 * **Important**: Results are automatically filtered to exclude YouTube Topic channels
 * (auto-generated channels with "topic" in the title or description). This filtering
 * happens in the service layer via the `filterTopicChannels` function.
 * 
 * @see filterTopicChannels in src/services/outreach/integrations/youtubeSearch.ts
 */
export interface YouTubeSearchResult {
  /** YouTube channel unique identifier */
  channelId: string;
  /** Channel display name - filtered to exclude channels containing "topic" */
  channelTitle: string;
  /** Channel description text - filtered to exclude channels containing "topic" */
  channelDescription: string;
  /** URL to the channel's thumbnail image */
  thumbnailUrl: string;
  /** Number of subscribers (optional, requires additional API call) */
  subscriberCount?: number;
  /** Total view count across all videos (optional, requires additional API call) */
  viewCount?: number;
  /** Total number of videos on the channel (optional, requires additional API call) */
  videoCount?: number;
}

/**
 * YouTube search response with pagination metadata.
 * 
 * **Important**: The `items` array contains only non-Topic channels. YouTube Topic
 * channels (auto-generated channels) are filtered out during search processing.
 * The `totalResults` reflects the count of items after filtering, not the raw API response count.
 * 
 * @see YouTubeSearchResult for details on filtered channel data
 * @see filterTopicChannels in src/services/outreach/integrations/youtubeSearch.ts
 */
export interface YouTubeSearchResponse {
  /** Array of channel search results with Topic channels filtered out */
  items: YouTubeSearchResult[];
  /** Token for fetching the next page of results (if available) */
  nextPageToken?: string;
  /** Token for fetching the previous page of results (if available) */
  prevPageToken?: string;
  /** Total number of results after Topic channel filtering */
  totalResults?: number;
  /** Number of results per page (as requested in the search parameters) */
  resultsPerPage: number;
}

// Detailed YouTube channel information fetched from the YouTube API
export interface YouTubeChannelSearchDetails {
  channelId: string;
  title: string;
  description: string;
  thumbnails: {
    default: { url: string };
    medium: { url: string };
    high: { url: string };
  };
  subscriberCount: string;
  viewCount: number;
  videoCount: number;
  email?: string;
  customUrl?: string;
  country?: string;
  publishedAt: string;
}

export interface SpotifyCategory {
  id: string;
  name: string;
  icons: Array<{ url: string; height: number; width: number }>;
}

export interface SpotifyPlaylistSearchResult {
  id: string;
  name: string;
  description: string;
  images: Array<{ url: string; height: number; width: number }>;
  owner: {
    display_name: string;
    id: string;
  };
  followers?: { total: number };
  tracks: { total: number };
  external_urls: { spotify: string };
  public?: boolean;
  collaborative?: boolean;
}

export interface SpotifyPlaylistDetails extends SpotifyPlaylistSearchResult {
  public: boolean;
  collaborative: boolean;
  followers: { total: number };
  snapshot_id: string;
  uri: string;
  tracks: { total: number };
}

export type SocialPlatform =
  | "instagram"
  | "twitter"
  | "youtube"
  | "tiktok"
  | "facebook"
  | "website"
  | "other";

export interface SocialLink {
  platform: SocialPlatform;
  handle: string;
  url?: string;
  label?: string;
}

// Recipient metadata type for enhanced campaign storage
export interface RecipientMetadata {
  id: string;
  category: string;
  // Denormalized fields to avoid N+1 query pattern
  name?: string;           // channelName/playlistName/blogName
  email?: string;          // email/curatorEmail/contact
  url?: string;            // channelUrl/playlistUrl/blogUrl
  // Optional metadata for display
  subscriberCount?: number;
  socialLinks?: SocialLink[];
  socialHandles?: string;
}

// Tag type shared across outreach modules
export interface Tag {
  id: string;
  name: string;
  description?: string;
  category: OutreachCategory | "global";
  color?: string;
  createdAt: string;
  updatedAt: string;
  usageCount: number;
  createdBy?: string;
}

// Campaign type
export interface Campaign extends OutreachItemBase {
  name: string;
  description?: string;
  recipients: string[] | RecipientMetadata[]; // Array of recipient IDs or enhanced metadata
  messageSubject?: string;
  messageBody?: string;
  messageTo?: string; // Optional override for recipients
  videoUrl?: string; // YouTube video URL for promotion
  videoThumbnail?: string; // Stored thumbnail URL
  createdAt: string;
  updatedAt: string;
  saved: boolean; // Flag to indicate if the campaign has been saved
  tag?: string | Tag | null; // Optional organizational tag
  tagId?: string | null; // Persisted reference to the selected tag
  tags?: string[]; // Additional tags applied to the campaign
  tagIds?: string[]; // Persisted references when multiple tags are assigned
}

// Reaction Channel type
export interface ReactionChannel extends OutreachItemBase {
  channelName: string;
  channelUrl: string;
  subscriberCount: string;
  email: string;
  socialHandles: string;
  socialLinks?: SocialLink[];
  // Analytics data
  viewCount?: number;
  videoCount?: number;
  likes?: string; // New field for total likes
  lastUpdated?: string;
  // Discovery Optimization Fields (New)
  discoverySource?: 'curator_discovery' | 'manual' | 'import';
  contactEmail?: string | null;
  discoveredAt?: string; // ISO timestamp
  lastValidated?: string; // ISO timestamp
  preQualified?: boolean;
  rejectionReason?: string | null;
}

// Playlisting Channel type
export interface PlaylistingChannel extends OutreachItemBase {
  channelName: string;
  channelUrl: string;
  subscriberCount: string;
  email: string;
  // Analytics data
  viewCount?: number;
  videoCount?: number;
  likes?: string; // New field for total likes
  lastUpdated?: string;
  // Discovery Optimization Fields (New)
  discoverySource?: 'curator_discovery' | 'manual' | 'import';
  contactEmail?: string | null;
  discoveredAt?: string; // ISO timestamp
  lastValidated?: string; // ISO timestamp
  preQualified?: boolean;
  rejectionReason?: string | null;
}

// Spotify Playlist type
export interface SpotifyPlaylist extends OutreachItemBase {
  playlistName: string;
  playlistUrl: string;
  curatorSocialHandle: string;
  source: string;
  curatorEmail: string;
  playlistLikes: number;
  playlistFollowers: number;
  // Analytics data
  trackCount?: number;
  lastUpdated?: string;
}

// Music Blog type
export interface MusicBlog extends OutreachItemBase {
  blogName: string;
  contact: string;
  blogUrl: string;
  monthlyVisits: number;
  lastUpdated?: string;
}

// Music Industry type
export interface MusicIndustry extends OutreachItemBase {
  company: string;
  contact: string;
  email: string;
  siteUrl: string;
  notes: string;
}

// Genre options
export const genreOptions = [
  "Alternative",
  "Hip Hop",
  "Pop",
  "R&B",
  "Latin",
  "World",
  "Reggae",
  "Metal",
  "Rock",
  "Country",
  "EDM",
  "Any",
];

// Outreach category types
export enum OutreachCategory {
  REACTION_CHANNELS = "reactionChannels",
  PLAYLISTING_CHANNELS = "playlistingChannels",
  SPOTIFY_PLAYLISTS = "spotifyPlaylists",
  MUSIC_BLOGS = "musicBlogs",
  MUSIC_INDUSTRY = "musicIndustry",
  CAMPAIGNS = "campaigns",
  MESSAGE_CORRELATIONS = "messageCorrelations", // Internal use only, not displayed in UI
}

// Map category to display name
export const categoryDisplayNames: Record<OutreachCategory, string> = {
  [OutreachCategory.REACTION_CHANNELS]: "YouTube Reaction Channels",
  [OutreachCategory.PLAYLISTING_CHANNELS]: "YouTube Playlisting Channels",
  [OutreachCategory.SPOTIFY_PLAYLISTS]: "Spotify Playlists",
  [OutreachCategory.MUSIC_BLOGS]: "Music Blogs",
  [OutreachCategory.MUSIC_INDUSTRY]: "Music Industry",
  [OutreachCategory.CAMPAIGNS]: "Campaigns",
  [OutreachCategory.MESSAGE_CORRELATIONS]: "Message Correlations",
};
